import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Plug, RotateCcw } from 'lucide-react';
import { PageLayout } from '../../../../components/ui/PageLayout';
import Button from '@/components/ui/Button';
import { useToast, ToastRenderer } from '@/hooks/useToast';
import type { RootState } from '../../../../store/redux/store/store';
import { useIntegrationDemo } from './demoStore';
import { DataTypeKey } from './types';
import ActivityTab from './components/ActivityTab';
import ConfigurationTab from './components/ConfigurationTab';
import DataExchangeSummary from './components/DataExchangeSummary';
import FieldMappingPanel from './components/FieldMappingPanel';
import ImportPreviewDialog from './components/ImportPreviewDialog';
import IntegrationPointsTab from './components/IntegrationPointsTab';

type TabKey = 'points' | 'configuration' | 'activity';

const TAB_ORDER: TabKey[] = ['points', 'configuration', 'activity'];

const ExternalIntegrations: React.FC = () => {
  const { t } = useTranslation();
  const demo = useIntegrationDemo();
  const { toast, showToast, dismissToast } = useToast();
  const fullName = useSelector((state: RootState) => state.user.fullName as string | undefined);
  const actorName = fullName ?? (t('extInt.actor.fallback') as string);

  const [activeTab, setActiveTab] = useState<TabKey>('points');
  const [mappingFor, setMappingFor] = useState<DataTypeKey | null>(null);
  const [previewFor, setPreviewFor] = useState<DataTypeKey | null>(null);
  const [exchangeFor, setExchangeFor] = useState<string | null>(null);

  const sourceFor = (dataType: DataTypeKey | null) => {
    if (!dataType) return undefined;
    const point = demo.state.points.find((p) => p.dataType === dataType);
    return demo.state.sources.find((s) => s.id === point?.sourceId);
  };

  return (
    <PageLayout
      title={t('extInt.title')}
      description={t('extInt.subtitle')}
      icon={Plug}
      accentColor="blue"
      maxWidth="1600px"
      headerActions={
        <Button variant="outline" onClick={demo.reset} data-testid="reset-demo-btn">
          <RotateCcw className="me-2 h-4 w-4" aria-hidden="true" />
          {t('extInt.resetDemo')}
        </Button>
      }
    >
      <div className="space-y-6">
        <div className="flex flex-wrap border-b border-border" role="tablist">
          {TAB_ORDER.map((key) => {
            const isActive = activeTab === key;
            return (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveTab(key)}
                className={`inline-flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                  isActive
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:border-border hover:text-foreground'
                }`}
                data-testid={`tab-${key}`}
              >
                {t(`extInt.tab.${key}`)}
              </button>
            );
          })}
        </div>

        {activeTab === 'points' && (
          <IntegrationPointsTab
            demo={demo}
            actorName={actorName}
            onOpenMapping={setMappingFor}
            onOpenPreview={setPreviewFor}
          />
        )}
        {activeTab === 'configuration' && (
          <ConfigurationTab demo={demo} actorName={actorName} onViewExchange={setExchangeFor} />
        )}
        {activeTab === 'activity' && (
          <ActivityTab runs={demo.state.syncRuns} sources={demo.state.sources} />
        )}
      </div>

      <FieldMappingPanel
        dataType={mappingFor}
        source={sourceFor(mappingFor)}
        demo={demo}
        onClose={() => setMappingFor(null)}
      />
      <ImportPreviewDialog
        dataType={previewFor}
        source={sourceFor(previewFor)}
        demo={demo}
        actorName={actorName}
        onClose={() => setPreviewFor(null)}
        onImported={() => showToast(t('extInt.toast.imported') as string, 'success')}
        onFixMapping={() => { setMappingFor(previewFor); setPreviewFor(null); }}
      />
      <DataExchangeSummary
        source={demo.state.sources.find((s) => s.id === exchangeFor)}
        actorName={actorName}
        onSign={(id) => demo.signDataExchange(id, actorName)}
        onClose={() => setExchangeFor(null)}
      />

      <ToastRenderer toast={toast} onDismiss={dismissToast} />
    </PageLayout>
  );
};

export default ExternalIntegrations;
